import { useState } from 'react';
import { api } from '../services/api';
import type { SearchResult } from '../types';

interface LaneManagerProps {
    visibleLanes: string[];
    onLanesChange: (lanes: string[]) => void;
}

export function LaneManager({ visibleLanes, onLanesChange }: LaneManagerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [availableLanes, setAvailableLanes] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const loadAvailableLanes = async () => {
        try {
            setLoading(true);
            const response = await api.get<SearchResult[]>('/results/latest');

            // Extract unique keywords from all search results
            const lanes = new Set<string>();
            response.data.forEach(result => {
                result.keywords.forEach(keyword => lanes.add(keyword));
            });

            setAvailableLanes(Array.from(lanes).sort());
        } catch (err) {
            console.error('Error loading lanes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setIsOpen(true);
        loadAvailableLanes();
    };

    const toggleLane = (lane: string) => {
        const updated = visibleLanes.includes(lane)
            ? visibleLanes.filter(l => l !== lane)
            : [...visibleLanes, lane];

        onLanesChange(updated);
    };

    if (!isOpen) {
        return (
            <button className="lane-manager-btn" onClick={handleOpenModal}>
                Manage Lanes
            </button>
        );
    }

    return (
        <div className="modal-overlay" onClick={() => setIsOpen(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Manage Lanes</h2>
                    <button className="modal-close" onClick={() => setIsOpen(false)}>×</button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <p>Loading...</p>
                    ) : availableLanes.length === 0 ? (
                        <p>No lanes available</p>
                    ) : (
                        <div className="lane-buttons">
                            {availableLanes.map(lane => (
                                <button
                                    key={lane}
                                    className={`lane-toggle-btn ${visibleLanes.includes(lane) ? 'active' : ''}`}
                                    onClick={() => toggleLane(lane)}
                                >
                                    {lane}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}